import React from "react";
import { Button, Row, Col, Card, CardBody, ButtonToolbar } from "reactstrap";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { useMutation, useQuery } from "@apollo/client";
import { useHistory } from "react-router";
import { toast } from "react-toastify";
import { CREATE_EMPLOYEE_KPI } from "@/GraphQL/Mutation";
import {
  GET_ALL_KPI_BY_DEPARTMENT_ID,
  GET_ALL_KPI_DURATION,
  GET_EMPLOYEE_KPI_BY_EMPLOYEE_ID,
} from "@/GraphQL/Queries";
import { MultiSelectField } from "@/shared/components/form/MultiSelect";
import FormField from "../FormField";
import { SelectField } from "@/shared/components/form/Select";
import { FaPlus, FaTrash } from "react-icons/fa";
import moment from "moment";
import BreadCrumb from "@/shared/components/BreadCrumb/BreadCrumb";

export default function CreateEmployeeKpi() {
  const history = useHistory();
  const employee_id = history?.location?.state?.id;
  const deptID = history?.location?.state?.dept;

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      duration: null,
      kpis: [{ dept_kpi_id: null, target: "" }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "kpis",
  });

  const { data: kpiDurationData } = useQuery(GET_ALL_KPI_DURATION);

  const { data: kpiData } = useQuery(GET_ALL_KPI_BY_DEPARTMENT_ID, {
    variables: { dept_id: Number(deptID) },
    skip: !Number(deptID),
  });

  const [createEmployeeKpi, { loading }] = useMutation(CREATE_EMPLOYEE_KPI, {
    refetchQueries: [
      {
        query: GET_EMPLOYEE_KPI_BY_EMPLOYEE_ID,
        variables: { employee_id },
      },
    ],
  });

  const durationOptions = kpiDurationData?.getAllKpiDuration?.data?.map(
    (item) => ({
      label: `${moment(item.start_date).format("MMMM, YYYY")} to ${moment(
        item.end_date
      ).format("MMMM, YYYY")}`,
      value: item.id,
    })
  );

  const kpiOptions = kpiData?.getAllKpiByDepartmentId?.data?.map((item) => ({
    label: item.key_area,
    value: item.id,
  }));

  const kpiSelections = watch("kpis");

  const onSubmit = async (formData) => {
    try {
      await createEmployeeKpi({
        variables: {
          employee_id: Number(employee_id),
          duration_id: Number(formData?.duration?.value),
          dept_kpi_id: formData.kpis.map((item) => ({
            id: item?.dept_kpi_id?.value,
            target: Number(item?.target),
          })),
        },
      });

      toast.success("Employee KPI created successfully!");
      reset();
      history.goBack();
    } catch (err) {
      console.error("❌ Failed to create employee KPI:", err);
      toast.error("Failed to create Employee KPI.");
    }
  };
  const breadCrumbData = [
    {
      name: "Dashboard",
      link: "/",
    },
    // {
    //   name: "Member List",
    //   link: "/department/member",
    // },
    {
      name: "Kpi Duration",
    },
  ];

  return (
    <Col md={12} className="mx-auto">
      <Card>
        <CardBody>
          <Row>
            <Col>
              <BreadCrumb breadCrumbData={breadCrumbData} />
            </Col>
          </Row>
          <h4 className="mb-4">Create Employee KPI</h4>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Col md={8} className="mx-auto">
              <Row>
                <Col md={12}>
                  <div className="form__form-group">
                    <span className="form__form-group-label">Duration</span>
                    <div className="form__form-group-field">
                      <FormField
                        name="duration"
                        control={control}
                        component={SelectField}
                        errors={errors}
                        options={durationOptions}
                        rules={{ required: "Duration is required" }}
                        placeholder="Select Duration"
                      />
                    </div>
                  </div>
                </Col>
              </Row>

              <Row>
                {fields.map((item, index) => {
                  const selectedKpiIds = kpiSelections
                    ?.filter((_, i) => i !== index)
                    .map((k) => k?.dept_kpi_id?.value)
                    .filter(Boolean);

                  const filteredKpiOptions = kpiOptions?.filter(
                    (opt) => !selectedKpiIds.includes(opt.value)
                  );

                  return (
                    <React.Fragment key={item.id}>
                      <Col md={6}>
                        <div className="form__form-group">
                          <span className="form__form-group-label">KPI</span>
                          <div className="form__form-group-field">
                            <Controller
                              name={`kpis.${index}.dept_kpi_id`}
                              control={control}
                              rules={{ required: "KPI is required" }}
                              render={({ field }) => (
                                <MultiSelectField
                                  {...field}
                                  options={filteredKpiOptions}
                                  placeholder="Select KPI"
                                  isMulti={false}
                                  onChange={field.onChange}
                                />
                              )}
                            />
                            {errors.kpis?.[index]?.dept_kpi_id && (
                              <small className="text-danger">
                                {errors.kpis[index].dept_kpi_id.message}
                              </small>
                            )}
                          </div>
                        </div>
                      </Col>

                      <Col md={3}>
                        <div className="form__form-group">
                          <label className="form__form-group-label">
                            Target
                          </label>
                          <div className="form__form-group-field">
                            <input
                              type="number"
                              className="form-control"
                              placeholder="Target"
                              {...register(`kpis.${index}.target`, {
                                required: "Target is required",
                              })}
                            />
                            {errors.kpis?.[index]?.target && (
                              <small className="text-danger">
                                {errors.kpis[index].target.message}
                              </small>
                            )}
                          </div>
                        </div>
                      </Col>

                      <Col md={3} className="d-flex align-items-center mt-4">
                        {index === 0 ? (
                          <Button
                            color="success"
                            type="button"
                            onClick={() =>
                              append({ dept_kpi_id: null, target: "" })
                            }
                          >
                            <FaPlus />
                          </Button>
                        ) : (
                          <Button
                            color="danger"
                            type="button"
                            onClick={() => remove(index)}
                          >
                            <FaTrash />
                          </Button>
                        )}
                      </Col>
                    </React.Fragment>
                  );
                })}
              </Row>
              <div className="d-flex justify-content-end mt-4">
                <ButtonToolbar>
                  <Button
                    color="danger"
                    onClick={() => history.goBack()}
                    type="button"
                  >
                    Cancel
                  </Button>
                  <Button color="primary" type="submit" disabled={loading}>
                    {loading ? "Submitting..." : "Submit"}
                  </Button>
                </ButtonToolbar>
              </div>
            </Col>
          </form>
        </CardBody>
      </Card>
    </Col>
  );
}
